import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Menu, MenuItem } from '@mui/material'
import { RequiredPermission } from 'app-support/permissions/RequiredPermissions'
import { PERMISSION_APP_ACCESS } from 'app-support/permissions/Permissions'
import { IRootReducerState } from 'state/reducers'
import { useDispatch, useSelector } from 'react-redux'
import { setRegion } from 'core/state/region/actions'

const RegionSelector: React.FC<{}> = () => {
  const dispatch = useDispatch()
  const { i18n, t } = useTranslation()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), [setAnchorEl])
  const handleClose = useCallback(() => setAnchorEl(null), [setAnchorEl])

  const selectedRegion = useSelector((state: IRootReducerState) => state.region.selectedRegion)
  const allowedRegions = useSelector((state: IRootReducerState) => state.region.allowedRegions)
  const roles = useSelector((state: IRootReducerState) => state.auth.user.roles)
  const hasInitializedRegion = React.useRef(false)

  useEffect(() => {
    const dachUsersRoles = roles?.filter(role => role.startsWith('dach_'))

    if (!hasInitializedRegion.current && dachUsersRoles && dachUsersRoles.length > 0 && allowedRegions.length > 0) {
      const dachRegion = allowedRegions.find(region => region.toLowerCase().includes('de.carsys.online'))
      if (dachRegion) {
        hasInitializedRegion.current = true
        dispatch(setRegion(dachRegion))
      }
    }
  }, [roles, allowedRegions, dispatch])

  const regionOptions = React.useMemo(
    () =>
      allowedRegions.map(region => {
        const environmentKey = `global.region_${region}`
        // use literal region name if translation key is not found
        const environment = i18n.exists(environmentKey) ? t(environmentKey) : region

        return {
          key: region,
          value: region,
          text: environment
        }
      }),
    [allowedRegions, i18n.language]
  )

  const currentRegionOption = useMemo(
    () => regionOptions.find(o => o.value === selectedRegion)?.text || '.....',
    [regionOptions, selectedRegion]
  )
  const changeRegion = useCallback((value: string) => dispatch(setRegion(value)), [dispatch])
  return (
    <RequiredPermission permission={PERMISSION_APP_ACCESS}>
      <Button sx={{ height: '100%' }} color='secondary' data-testid='header-dropdown-regions' onClick={handleClick}>
        <Box sx={{ paddingLeft: 1 }}>{currentRegionOption}</Box>
      </Button>
      <Menu open={open} onClose={handleClose} anchorEl={anchorEl}>
        {regionOptions.map(option => (
          <MenuItem key={option.value} onClick={() => changeRegion(option.value)} data-testid='header-dropdown-regions-option'>
            <Box sx={{ paddingLeft: 1 }}>{option.text}</Box>
          </MenuItem>
        ))}
      </Menu>
    </RequiredPermission>
  )
}

export default RegionSelector
